import Phaser from "phaser";

import { CATALOG, GESTURES, gestureById, type GestureDef } from "../avatar/catalog";
import { DEPTH } from "../config";
import { AuraEffect, ChargeBar } from "./Aura";
import { DIRECTIONS, type PlayerInfo, type PlayerState } from "../types";

const BUBBLE_MS = 4500;
/** Distancias a partir do pe do avatar (o frame tem 48px, a cabeca vai ate ~44). */
const LABEL_OFFSET = -47;
/**
 * Os nomes ficam acima de arvores, postes e coberturas (como nos jogos do
 * genero), mas ainda ordenados entre si pelo y e abaixo da interface.
 */
const LABEL_DEPTH = DEPTH.overhead + 10;
const BUBBLE_OFFSET = -64;
const BUBBLE_RADIUS = 8;
const BUBBLE_TAIL_WIDTH = 10;
const BUBBLE_TAIL_HEIGHT = 6;
const LABEL_PAD_X = 5;
const LABEL_PAD_Y = 2;
export const FONT = '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif';

/** Ciclo do pulo: agacha, sobe e cai, e fica um instante parado antes do proximo. */
const HOP_MS = 900;
const HOP_CROUCH_MS = 120;
const HOP_AIR_MS = 480;
const HOP_LAND_MS = 120;
const HOP_HEIGHT = 12;
/** Intervalo entre os emojis dos gestos de emoji (coracao, risada). */
const EMOTE_EVERY_MS = 700;
const EMOTE_RISE = 22;
const EMOTE_MS = 1400;

/** Texto nitido: acompanha o zoom da camera e a densidade de pixels da tela. */
export function textResolution(zoom: number): number {
  return Math.max(1, Math.round(zoom)) * Math.min(3, Math.max(1, Math.ceil(window.devicePixelRatio || 1)));
}

/**
 * Um avatar no Jardim: sprite animado + etiqueta com o nome + balao de fala.
 *
 * Jogadores remotos sao interpolados na direcao da ultima posicao recebida do
 * servidor; o avatar local usa predicao (ver GardenScene) e so e corrigido
 * quando a divergencia passa do limite.
 */
export class Avatar {
  readonly id: string;
  readonly sprite: Phaser.GameObjects.Sprite;

  private readonly label: Phaser.GameObjects.Text;
  /** Pilula escura atras do nome, como os crachas do Gather. */
  private readonly labelBg: Phaser.GameObjects.Graphics;
  private readonly bubble: Phaser.GameObjects.Text;
  private readonly bubbleBg: Phaser.GameObjects.Graphics;
  private bubbleTimer?: Phaser.Time.TimerEvent;
  /** Sombra no chao enquanto o avatar esta no ar (o quadro do pulo nao tem sombra). */
  private readonly hopShadow: Phaser.GameObjects.Graphics;
  private emoteTimer?: Phaser.Time.TimerEvent;
  private readonly emotes = new Set<Phaser.GameObjects.Text>();

  /**
   * Posicao do pe no chao. O sprite pode estar acima dela (pulo); a camera,
   * a profundidade e a interpolacao usam esta.
   */
  readonly anchor: { x: number; y: number };

  private readonly textureKey: string;
  private direction = 0;
  private moving = false;
  /** id do assento ocupado (0 = de pe). */
  seat = 0;
  /** Gesto em loop (ausente = nenhum). */
  private gestureDef?: GestureDef;
  private gestureStart = 0;
  /** Aura ativa (easter egg) e a barrinha de carga, so no avatar local. */
  private aura?: AuraEffect;
  /** O primeiro estado so sincroniza: a explosao fica para quando a aura acende na hora. */
  private auraSynced = false;
  private chargeBar?: ChargeBar;
  /** Chat de voz: falando agora (contorno verde no cracha) e em conversa trancada (cadeado). */
  private speaking = false;
  private locked = "";
  private readonly name: string;

  /** Posicao alvo vinda do servidor (usada na interpolacao dos remotos). */
  targetX: number;
  targetY: number;

  constructor(
    private readonly scene: Phaser.Scene,
    info: PlayerInfo,
    private readonly isLocal: boolean,
    textureKey: string,
  ) {
    this.id = info.id;
    this.name = info.name;
    this.textureKey = textureKey;
    this.targetX = info.x;
    this.targetY = info.y;
    this.anchor = { x: info.x, y: info.y };

    // A resolucao do texto acompanha o zoom da camera: assim o nome sai nitido
    // sem virar um borrao quando a tela (e o zoom) mudam de tamanho.
    const resolution = textResolution(scene.cameras.main.zoom);

    this.sprite = scene.add
      .sprite(info.x, info.y, textureKey, 0)
      .setOrigin(0.5, 1) // a ancora e o pe do avatar, igual ao servidor
      .setDepth(DEPTH.actors + info.y);

    this.label = scene.add
      .text(info.x, info.y + LABEL_OFFSET, info.name, {
        fontFamily: FONT,
        fontSize: "10px",
        fontStyle: "bold",
        color: "#ffffff",
        resolution,
      })
      .setOrigin(0.5, 1)
      .setDepth(LABEL_DEPTH + info.y + 2);

    this.labelBg = scene.add.graphics().setDepth(LABEL_DEPTH + info.y + 1);
    this.redrawLabelBg();

    this.bubbleBg = scene.add.graphics().setDepth(DEPTH.ui).setVisible(false);
    this.hopShadow = scene.add.graphics().setVisible(false);

    this.bubble = scene.add
      .text(info.x, info.y + BUBBLE_OFFSET, "", {
        fontFamily: FONT,
        fontSize: "10px",
        color: "#2a2838",
        padding: { x: 7, y: 5 },
        align: "center",
        wordWrap: { width: 180 },
        resolution,
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.ui + 1)
      .setVisible(false);
  }

  /** Guarda o estado recebido do servidor sem mexer na posicao desenhada. */
  applyServerState(state: PlayerState): void {
    this.targetX = state.x;
    this.targetY = state.y;
    this.direction = state.d;
    this.moving = state.m;
    this.seat = state.s ?? 0;
    this.setGesture(state.g);
    this.setAura(state.a);
  }

  /** Contorno verde no cracha enquanto a pessoa fala no chat de voz. */
  setSpeaking(speaking: boolean): void {
    if (speaking === this.speaking) return;
    this.speaking = speaking;
    this.redrawLabelBg();
  }

  /** Cadeado antes do nome de quem esta numa conversa trancada (id dela; "" = nenhuma). */
  setLocked(locked: string | undefined): void {
    locked ??= "";
    if (locked === this.locked) return;
    const changed = !!locked !== !!this.locked;
    this.locked = locked;
    if (!changed || !this.name) return;
    this.label.setText(locked ? `🔒 ${this.name}` : this.name);
    this.redrawLabelBg();
  }

  /** Conversa trancada de que participa ("" = nenhuma). */
  get lockedId(): string {
    return this.locked;
  }

  /** Liga, troca ou desliga a aura; acender na hora (nao ao entrar) explode. */
  setAura(id: string | undefined): void {
    const first = !this.auraSynced;
    this.auraSynced = true;
    if (id === this.aura?.id) return;
    this.aura?.destroy();
    this.aura = undefined;
    if (!id) return;
    this.aura = new AuraEffect(this.scene, id);
    this.aura.update(this.anchor.x, this.anchor.y, this.sprite.depth);
    if (!first) {
      this.aura.burst();
      if (this.isLocal) this.scene.cameras.main.shake(300, 0.005);
    }
  }

  /** Progresso da carga de aura (0 = sem barrinha). */
  setCharge(progress: number): void {
    if (!this.chargeBar && progress <= 0) return;
    this.chargeBar ??= new ChargeBar(this.scene);
    this.chargeBar.set(progress);
  }

  /** Move o avatar local diretamente (a predicao ja calculou a posicao). */
  setPosition(x: number, y: number): void {
    this.anchor.x = x;
    this.anchor.y = y;
  }

  get gesture(): string | undefined {
    return this.gestureDef?.id;
  }

  /** Comeca (ou troca) o gesto em loop; undefined para. */
  setGesture(id: string | undefined): void {
    const def = gestureById(id);
    if (def?.id === this.gestureDef?.id) return;
    this.gestureDef = def;
    this.gestureStart = this.scene.time.now;

    this.emoteTimer?.remove();
    this.emoteTimer = undefined;
    const emote = def?.emote;
    if (emote) {
      this.spawnEmote(emote);
      this.emoteTimer = this.scene.time.addEvent({
        delay: EMOTE_EVERY_MS,
        loop: true,
        callback: () => this.spawnEmote(emote),
      });
    }
  }

  setFacing(direction: number, moving: boolean): void {
    this.direction = direction;
    this.moving = moving;
  }

  update(delta: number): void {
    if (!this.isLocal) {
      // Interpolacao exponencial: converge rapido sem estalos quando chega um
      // snapshot novo, e continua suave se um pacote atrasar.
      const t = 1 - Math.exp((-delta / 1000) * 14);
      const dx = this.targetX - this.anchor.x;
      const dy = this.targetY - this.anchor.y;

      if (Math.abs(dx) > 192 || Math.abs(dy) > 192) {
        this.setPosition(this.targetX, this.targetY); // teleporte/respawn
      } else {
        this.setPosition(this.anchor.x + dx * t, this.anchor.y + dy * t);
      }
    }

    const lift = this.playAnimation();

    const { x, y } = this.anchor;
    this.sprite.setPosition(x, y - lift).setDepth(DEPTH.actors + y);
    this.updateHopShadow(lift);
    this.aura?.update(x, y - lift, this.sprite.depth);
    this.chargeBar?.update(x, y, LABEL_DEPTH + y, delta, this.scene.time.now);
    const lx = Math.round(x);
    const ly = Math.round(y - lift) + LABEL_OFFSET;
    this.label.setPosition(lx, ly).setDepth(LABEL_DEPTH + y + 2);
    this.labelBg.setPosition(lx, ly).setDepth(LABEL_DEPTH + y + 1);
    if (this.bubble.visible) {
      const bx = Math.round(x);
      const by = Math.round(y - lift) + BUBBLE_OFFSET;
      this.bubble.setPosition(bx, by);
      this.bubbleBg.setPosition(bx, by);
    }
  }

  /** Escolhe a animacao do momento; devolve quanto o sprite sobe (pulo). */
  private playAnimation(): number {
    const dir = DIRECTIONS[this.direction] ?? "down";
    const pose = this.seat || this.moving ? undefined : this.gestureDef;
    if (pose?.hop && pose.frames) return this.playHop(pose.frames);

    let key = this.seat ? `${this.textureKey}-sit` : `${this.textureKey}-${this.moving ? "walk" : "idle"}-${dir}`;
    if (pose?.frames && this.scene.anims.exists(`${this.textureKey}-g-${pose.id}`)) key = `${this.textureKey}-g-${pose.id}`;
    this.play(key);
    return 0;
  }

  private play(key: string): void {
    const anims = this.sprite.anims;
    if (!anims.isPlaying || anims.currentAnim?.key !== key) this.sprite.play(key);
  }

  /** Pulo em loop: os quadros sao [agachado, no ar]; a altura vem de uma parabola. */
  private playHop([crouch, air]: number[]): number {
    const texture = this.sprite.texture;
    if (!texture.has(String(crouch)) || !texture.has(String(air))) {
      this.play(`${this.textureKey}-idle-down`); // spritesheet sem gestos
      return 0;
    }
    const t = (this.scene.time.now - this.gestureStart) % HOP_MS;
    let frame = 0;
    let lift = 0;
    if (t < HOP_CROUCH_MS) frame = crouch;
    else if (t < HOP_CROUCH_MS + HOP_AIR_MS) {
      frame = air;
      lift = Math.sin((Math.PI * (t - HOP_CROUCH_MS)) / HOP_AIR_MS) * HOP_HEIGHT;
    } else if (t < HOP_CROUCH_MS + HOP_AIR_MS + HOP_LAND_MS) frame = crouch;
    this.sprite.anims.stop();
    this.sprite.setFrame(frame);
    return Math.round(lift);
  }

  private updateHopShadow(lift: number): void {
    if (lift <= 0) {
      this.hopShadow.setVisible(false);
      return;
    }
    // a sombra encolhe e clareia conforme o avatar sobe
    const k = 1 - lift / (HOP_HEIGHT * 2.5);
    this.hopShadow.clear();
    this.hopShadow.fillStyle(0x000000, 0.24 * k);
    this.hopShadow.fillEllipse(0, 0, 18 * k, 5 * k);
    this.hopShadow
      .setPosition(Math.round(this.anchor.x) + 0.5, Math.round(this.anchor.y) - 1)
      .setDepth(DEPTH.actors + this.anchor.y - 1)
      .setVisible(true);
  }

  /** Um emoji sobe da cabeca e some (gestos de emoji). */
  private spawnEmote(emote: string): void {
    const x = Math.round(this.anchor.x + Phaser.Math.Between(-7, 7));
    const y = Math.round(this.anchor.y) + LABEL_OFFSET - 6;
    const text = this.scene.add
      .text(x, y, emote, { fontFamily: FONT, fontSize: "12px", resolution: textResolution(this.scene.cameras.main.zoom) })
      .setOrigin(0.5, 1)
      .setDepth(LABEL_DEPTH + this.anchor.y + 3)
      .setScale(0.5);
    this.emotes.add(text);
    this.scene.tweens.add({ targets: text, scale: 1, duration: 180, ease: "Back.Out" });
    this.scene.tweens.add({
      targets: text,
      y: y - EMOTE_RISE,
      alpha: { from: 1, to: 0 },
      duration: EMOTE_MS,
      ease: "Sine.In",
      onComplete: () => {
        this.emotes.delete(text);
        text.destroy();
      },
    });
  }

  /** Reajusta a nitidez dos textos quando o zoom da camera muda (girar a tela). */
  applyZoom(zoom: number): void {
    const resolution = textResolution(zoom);
    this.label.setResolution(resolution);
    this.bubble.setResolution(resolution);
  }

  say(text: string): void {
    this.bubble.setText(text).setVisible(true);
    this.redrawBubbleBg();
    this.bubbleBg.setVisible(true);
    this.bubbleTimer?.remove();
    this.bubbleTimer = this.scene.time.delayedCall(BUBBLE_MS, () => {
      this.bubble.setVisible(false);
      this.bubbleBg.setVisible(false);
    });
  }

  /** Pilula do nome; o jogador local ganha um pontinho verde, como no Gather. */
  private redrawLabelBg(): void {
    const w = Math.ceil(this.label.width) + LABEL_PAD_X * 2 + (this.isLocal ? 7 : 0);
    const h = Math.ceil(this.label.height) + LABEL_PAD_Y * 2;
    const left = -w / 2;
    this.labelBg.clear();
    this.labelBg.fillStyle(0x24223a, 0.78);
    this.labelBg.fillRoundedRect(left, -h + LABEL_PAD_Y, w, h, h / 2);
    if (this.speaking) {
      this.labelBg.lineStyle(1.5, 0x5ee08a, 1);
      this.labelBg.strokeRoundedRect(left - 1, -h + LABEL_PAD_Y - 1, w + 2, h + 2, (h + 2) / 2);
    }
    if (this.isLocal) {
      this.labelBg.fillStyle(0x5ee08a, 1);
      this.labelBg.fillCircle(left + LABEL_PAD_X + 2, -h / 2 + LABEL_PAD_Y, 2.5);
      this.label.setOrigin(0.5 - 7 / 2 / this.label.width, 1);
    }
  }

  /** Desenha o balao (retangulo arredondado + rabicho) do tamanho do texto atual. */
  private redrawBubbleBg(): void {
    const w = this.bubble.width;
    const h = this.bubble.height;

    // O texto tem origem (0.5, 1): o retangulo fica acima do ponto (0, 0)
    // local, e o rabicho aponta pra baixo, na direcao da cabeca do avatar.
    this.bubbleBg.clear();
    this.bubbleBg.fillStyle(0x24223a, 0.18);
    this.bubbleBg.fillRoundedRect(-w / 2, -h + 2, w, h, BUBBLE_RADIUS);
    this.bubbleBg.fillStyle(0xffffff, 1);
    this.bubbleBg.fillRoundedRect(-w / 2, -h, w, h, BUBBLE_RADIUS);
    this.bubbleBg.fillTriangle(
      -BUBBLE_TAIL_WIDTH / 2,
      0,
      BUBBLE_TAIL_WIDTH / 2,
      0,
      0,
      BUBBLE_TAIL_HEIGHT,
    );
  }

  /** O ponto do mundo cai sobre o avatar ou o cracha com o nome (clique no mapa). */
  hit(x: number, y: number): boolean {
    return this.sprite.getBounds().contains(x, y) || (this.label.visible && this.label.getBounds().contains(x, y));
  }

  destroy(): void {
    this.bubbleTimer?.remove();
    this.emoteTimer?.remove();
    for (const text of this.emotes) text.destroy();
    this.emotes.clear();
    this.hopShadow.destroy();
    this.aura?.destroy();
    this.chargeBar?.destroy();
    this.sprite.destroy();
    this.label.destroy();
    this.labelBg.destroy();
    this.bubble.destroy();
    this.bubbleBg.destroy();
  }
}

/**
 * Registra as animacoes de um spritesheet de avatar. O grid vem do catalogo:
 * 5 colunas (parado + 4 quadros de caminhada) x linhas down, left, right, up,
 * sentado e as dos gestos (quadros listados em CATALOG.gestures).
 */
export function registerAvatarAnimations(scene: Phaser.Scene, textureKey: string): void {
  const { cols } = CATALOG.frame;
  DIRECTIONS.forEach((dir, row) => {
    const base = row * cols;
    const idleKey = `${textureKey}-idle-${dir}`;
    const walkKey = `${textureKey}-walk-${dir}`;

    if (!scene.anims.exists(idleKey)) {
      scene.anims.create({
        key: idleKey,
        frames: [{ key: textureKey, frame: base }],
        frameRate: 1,
        repeat: -1,
      });
    }
    if (!scene.anims.exists(walkKey)) {
      scene.anims.create({
        key: walkKey,
        frames: [base + 1, base + 2, base + 3, base + 4].map((frame) => ({ key: textureKey, frame })),
        frameRate: 8,
        repeat: -1,
      });
    }
  });

  const sitKey = `${textureKey}-sit`;
  if (!scene.anims.exists(sitKey)) {
    scene.anims.create({
      key: sitKey,
      frames: [{ key: textureKey, frame: DIRECTIONS.length * cols }],
      frameRate: 1,
      repeat: -1,
    });
  }

  // gestos com pose em loop; o pulo e montado quadro a quadro no Avatar
  const texture = scene.textures.get(textureKey);
  for (const g of GESTURES) {
    const key = `${textureKey}-g-${g.id}`;
    if (!g.frames || g.hop || scene.anims.exists(key)) continue;
    if (!g.frames.every((frame) => texture.has(String(frame)))) continue; // spritesheet antigo
    scene.anims.create({
      key,
      frames: g.frames.map((frame) => ({ key: textureKey, frame })),
      frameRate: g.fps ?? 4,
      repeat: -1,
    });
  }
}
